/**
 * Listing ADT — Phase 1 seed (purely additive).
 *
 * Discriminator-hygiene refactor groundwork. Today `listings` carries five
 * independent discriminator mechanisms (varchar `listing_format`, enum `type`,
 * bool `is_chat_private`, bool `accepts_offers`, varchar `shipping_mode`) with
 * no domain ADT — every consumer narrows by ad-hoc booleans against snake_case
 * row fields. This module introduces the canonical discriminated union all
 * subsequent phases will swap consumers onto.
 *
 * **This file is additive in Phase 1.** No existing consumer reads from it
 * yet — the phase 2-7 commits in `shippo-listing-discriminator` materialise
 * the downstream sweep. The only Phase 1 obligation is the fitness pin in
 * `tests/unit/fitness-listing-adt-shape.test.ts` (per IMPACT §5a "Phase 1
 * fitness pin (REQUIRED)").
 *
 * Operator decisions (IMPACT §5, closed 2026-06-29):
 *  - Q1 NO new axis on roadmap → ADT seed scope minimal (no proactive variants)
 *  - Q2 `is_chat_private` becomes its own `ListingVisibility` enum, orthogonal
 *       to the sale model (option b)
 *  - Q8 lives in `@kraahl/contract` (extend, not new pkg)
 *  - Q9 `chat_private × shipping_mode='live_rate'` MUST remain legal — every
 *       variant carries `shipping: ListingShipping` with both modes available
 *
 * Variant shape per IMPACT §4a (Shippo addendum) + DISCRIMINATORS.md inventory:
 *   - 4 sale-model `format` values × `type='standard'` → 4 variants
 *   - `type='preorder'` is its own 5th variant (today's prod has 0
 *     preorder×non-fixed_price rows; modelling it as a 5th variant keeps the
 *     `switch (l.format)` exhaustiveness check tight without an FP-only
 *     internal cast)
 *
 * Single boundary parser:
 *   `parseListingFromRow(raw)` is the SoT for ADT construction. Hand-rolled
 *   `Listing` literals are forbidden outside this file (boundary discipline
 *   per IMPACT §5a — same role as `parseShippoRate` for the rate seam).
 *
 * @see .tondocs/listings/DISCRIMINATORS.md
 * @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §4a, §5, §5a
 * @see packages/contract/src/listing-create.ts:269 — inline format literal
 *      lifted into LISTING_FORMATS here
 * @see lib/preorder.ts — PreorderStatus today (Phase 2 swaps the import)
 * @see types/listing.ts:226-230 — local literal copies today (Phase 2)
 * @see lib/story-cards/types.ts:4 — local ListingFormat copy (Phase 2)
 * @see apps/mobile/components/sell-flow/types.ts:146,152,179 — mobile copies
 *      (Phase 2 — mirror swap)
 */

import { z } from "zod";

import { CARRIER_PROVIDERS } from "./carrier";
import { ISO_4217_CODES } from "./money";

// ---------------------------------------------------------------------------
// 1. Canonical literal unions
// ---------------------------------------------------------------------------

/**
 * Sale-model discriminator. Single SoT lifted from the inline literal at
 * `packages/contract/src/listing-create.ts:269`. All downstream switches on
 * "what kind of listing is this" must use this exact tuple — adding a value
 * MUST also extend every `switch (l.format) { default: unreachableListing(l) }`
 * (compiler enforces via `never`).
 */
export const LISTING_FORMATS = [
  "fixed_price",
  "fixed_price_with_offers",
  "offers_only",
  "auction",
] as const;
export type ListingFormat = (typeof LISTING_FORMATS)[number];

/**
 * Production-mode discriminator. Mirrors the `listing_type` Postgres enum
 * created in `20260417120000_preorder_feature.sql` and the local literal at
 * `lib/preorder.ts:3` + `types/listing.ts:228` (Phase 2 dedup).
 *
 * IMPACT §5 Q7 closed: NOT renaming the DB column `type → production_mode`
 * — under the ADT, the column name is moot at TS-level.
 */
export const LISTING_TYPES = ["standard", "preorder"] as const;
export type ListingType = (typeof LISTING_TYPES)[number];

/**
 * Visibility axis — formalised in Phase 4 (2026-06-29). Today
 * `listings.visibility` is the `listing_visibility` Postgres enum
 * created by `20260629210000_listing_visibility_enum.sql`; pre-Phase-4
 * data lived on the now-dropped `is_chat_private boolean` column.
 *
 * IMPACT §5 Q2 closed (b): formalise as its own enum, orthogonal to the
 * sale model (so future axes — subscription, raffle, bundle, etc. —
 * compose freely with visibility without combinatorial duplication of
 * `listing_format` values).
 *
 * `chat_private × live_rate` MUST remain legal (Q9) — `ListingShipping` is
 * composed into every variant independently of `visibility`.
 */
export const LISTING_VISIBILITIES = ["public", "chat_private"] as const;
export type ListingVisibility = (typeof LISTING_VISIBILITIES)[number];

/**
 * Preorder lifecycle status. Mirror of the `preorder_status` enum at
 * `lib/preorder.ts:4` + `types/listing.ts:230`. Phase 2 swaps the imports;
 * `PREORDER_STATUS_ORDER` (the ordered advance list in `lib/preorder.ts:13`)
 * stays in the domain layer for now — it is logic, not a wire shape.
 */
export const PREORDER_STATUSES = [
  "open",
  "funded",
  "in_production",
  "shipped",
] as const;
export type PreorderStatus = (typeof PREORDER_STATUSES)[number];

// ---------------------------------------------------------------------------
// 2. ListingShipping — sub-union for the shipping_mode axis
// ---------------------------------------------------------------------------

/**
 * Canonical parcel dimensions in the units locked 2026-06-22 (PLAN §A
 * "Parcel units"): mass in grams, distances in millimetres. Mirror of the
 * Zod refinement at `listing-create.ts:230-266` (all-or-none).
 */
export type ParcelDims = {
  weightGrams: number;
  lengthMm: number;
  widthMm: number;
  heightMm: number;
};

/**
 * Shipping axis discriminated by `mode`. Mirrors the flat
 * `(shipping_mode, carrier_provider, parcel_*)` quartet on `listings`:
 *  - `mode='legacy'`     ↔ `shipping_config` array of method ids,
 *                          `carrier_provider` null, `parcel_*` null
 *  - `mode='live_rate'`  ↔ `carrier_provider` non-null, all 4 parcel dims
 *                          required (DB CHECK + Zod refinement enforce)
 *
 * Composed into every `Listing` variant as `shipping: ListingShipping` —
 * including chat-private (IMPACT §5 Q9 — no type-level restriction).
 *
 * @see packages/contract/src/listing-create.ts:230-266 — refineLiveRateCoherence
 */
export type ListingShipping =
  | { mode: "legacy"; methods: string[] }
  | { mode: "live_rate"; provider: string; parcel: ParcelDims };

// ---------------------------------------------------------------------------
// 3. Listing — variant-by-format discriminated union
// ---------------------------------------------------------------------------

/**
 * Fields shared across every variant. The orthogonal axes (`visibility`,
 * `shipping`) compose freely — no type-level mutex between visibility and
 * shipping mode (Q9). Currency is typed as `string` here because the
 * underlying Postgres column is `text`; the parser narrows it through
 * `ISO_4217_CODES` so callers get the validated tuple at runtime.
 */
export type ListingBase = {
  id: string;
  sellerId: string;
  currency: string;
  visibility: ListingVisibility;
  shipping: ListingShipping;
};

export type FixedPriceListing = ListingBase & {
  format: "fixed_price";
  type: "standard";
  price: number;
};

export type FixedPriceWithOffersListing = ListingBase & {
  format: "fixed_price_with_offers";
  type: "standard";
  price: number;
};

export type OffersOnlyListing = ListingBase & {
  format: "offers_only";
  type: "standard";
};

export type AuctionListing = ListingBase & {
  format: "auction";
  type: "standard";
  startPrice: number;
  reservePrice: number | null;
  endAt: string;
};

/**
 * `type='preorder'` rows on prod are all `listing_format='fixed_price'`
 * (DISCRIMINATORS.md §6 / IMPACT §I — REST verified 2026-06-16, 0 rows
 * with preorder × non-fixed_price). Modelled as its own variant so the
 * `format` discriminator is the SINGLE branch axis on the union — keeps
 * exhaustiveness probes tight without an FP-only internal cast.
 */
export type PreorderListing = ListingBase & {
  format: "fixed_price";
  type: "preorder";
  price: number;
  preorderStatus: PreorderStatus;
  preorderDeadline: string | null;
};

export type Listing =
  | FixedPriceListing
  | FixedPriceWithOffersListing
  | OffersOnlyListing
  | AuctionListing
  | PreorderListing;

// ---------------------------------------------------------------------------
// 4. Boundary parser — single SoT for ADT construction
// ---------------------------------------------------------------------------

/**
 * Raw listing-row shape — snake_case mirror of the `listings` SELECT. The
 * parser is permissive on shape (extra columns are stripped by default in
 * Zod v4) but strict on every field it reads. Fields that the ADT does not
 * carry today are intentionally absent from the schema.
 *
 * Nullability mirrors prod (REST audit 2026-06-16):
 *  - `visibility` is non-null after Phase 4 (DEFAULT 'public'); pre-Phase-4
 *    callers reading legacy rows pass `null`/missing → collapses to `public`
 *  - `shipping_mode` is nullable on pre-WS3.1 rows (treated as `legacy`)
 *  - `parcel_*` are nullable on legacy rows
 *  - `preorder_*` are null on standard rows
 *  - Phase 5 (2026-06-30): `auction_*` columns moved off `listings` into
 *    the `listing_auctions` side table. The parser accepts BOTH the
 *    nested `listing_auctions: { start_price, reserve_price, end_at, ... }`
 *    embed (produced by a PostgREST `.select("..., listing_auctions(...)")`
 *    against the `listings` table) AND the flat aliased `auction_*` keys
 *    (produced by reading from the `listings_with_effective_published_at`
 *    view, which re-exposes the side table under the legacy names). The
 *    nested form is preferred when both are present.
 */
const RawListingAuctionEmbedSchema = z
  .object({
    start_price: z.number().nullish(),
    reserve_price: z.number().nullish(),
    end_at: z.string().nullish(),
  })
  .nullish();

const RawListingRowSchema = z.object({
  id: z.string(),
  seller_id: z.string(),
  currency: z.enum(ISO_4217_CODES),
  listing_format: z.enum(LISTING_FORMATS),
  /**
   * DB column is `type`; the schema accepts either `type` or `listing_type`
   * to ease the Phase 2 swap (some consumers normalise the alias upstream).
   * `null` / missing collapses to `'standard'` — consistent with the prod
   * default on the Postgres column (`DEFAULT 'standard' NOT NULL`).
   */
  type: z.enum(LISTING_TYPES).nullish(),
  listing_type: z.enum(LISTING_TYPES).nullish(),

  /**
   * Phase 4 (2026-06-29): `listings.visibility listing_visibility NOT NULL
   * DEFAULT 'public'` replaces the dropped `is_chat_private boolean`. The
   * parser accepts `null`/missing for pre-Phase-4 fixtures and historic
   * row snapshots — collapses to `public` (the DB default).
   */
  visibility: z.enum(LISTING_VISIBILITIES).nullish(),

  // Shipping axis. Legacy rows pre-WS3.1 have `shipping_mode=null`; we treat
  // that as `legacy` — same as the BEFORE-trigger default on the column.
  shipping_mode: z.enum(["legacy", "live_rate"]).nullish(),
  shipping_config: z.array(z.unknown()).nullish(),
  carrier_provider: z.enum(CARRIER_PROVIDERS).nullish(),
  parcel_weight_grams: z.number().nullish(),
  parcel_length_mm: z.number().nullish(),
  parcel_width_mm: z.number().nullish(),
  parcel_height_mm: z.number().nullish(),

  // Commerce — variant-specific (validated below per format)
  price: z.number().nullish(),
  // Phase 5 (2026-06-30): both shapes accepted (see RawListingRowSchema
  // header comment above). `listing_auctions` is the nested embed from a
  // direct `.from("listings").select("..., listing_auctions(...)")` query;
  // the flat aliased `auction_*` keys come from view-shaped rows.
  // PostgREST sometimes returns the embed as `T | T[]` depending on hint —
  // we accept both via the embed's own nullish leniency at the resolver
  // step in `parseListingFromRow`.
  listing_auctions: z.union([
    RawListingAuctionEmbedSchema,
    z.array(RawListingAuctionEmbedSchema),
  ]).nullish(),
  auction_start_price: z.number().nullish(),
  auction_reserve_price: z.number().nullish(),
  auction_end_at: z.string().nullish(),
  preorder_status: z.enum(PREORDER_STATUSES).nullish(),
  preorder_deadline: z.string().nullish(),
});

export type RawListingRow = z.infer<typeof RawListingRowSchema>;

/**
 * Parser failure surface. Single error class so callers can `try/catch`
 * specifically without losing the underlying message.
 */
export class ListingParseError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ListingParseError";
  }
}

function parseShippingFromRow(row: RawListingRow): ListingShipping {
  // Mirror of the DB CHECK `listings_provider_required_for_live_rate` and the
  // Zod refinement at listing-create.ts:230-266. Legacy is the default for
  // null/absent shipping_mode (matches the BEFORE-trigger default on the
  // listings column).
  if (row.shipping_mode === "live_rate") {
    if (row.carrier_provider == null) {
      throw new ListingParseError(
        "shipping_mode='live_rate' requires carrier_provider",
      );
    }
    if (
      row.parcel_weight_grams == null ||
      row.parcel_length_mm == null ||
      row.parcel_width_mm == null ||
      row.parcel_height_mm == null
    ) {
      throw new ListingParseError(
        "shipping_mode='live_rate' requires all 4 parcel dims (weight + 3 sides)",
      );
    }
    return {
      mode: "live_rate",
      provider: row.carrier_provider,
      parcel: {
        weightGrams: row.parcel_weight_grams,
        lengthMm: row.parcel_length_mm,
        widthMm: row.parcel_width_mm,
        heightMm: row.parcel_height_mm,
      },
    };
  }

  // Legacy — extract method-id strings from the raw shipping_config array.
  // Today's prod stores either ["uuid-1", "uuid-2"] or [{id: "uuid-1", ...}];
  // Phase 1 only extracts strings (the dominant shape) — Phase 2 will
  // normalise the object form into the same builder. Legacy listings legally
  // have `methods: []` (e.g. chat-private quick-create rows).
  const raw = row.shipping_config ?? [];
  const methods: string[] = raw.filter((m): m is string => typeof m === "string");
  return { mode: "legacy", methods };
}

function visibilityFromRow(row: RawListingRow): ListingVisibility {
  // Phase 4 (2026-06-29): read directly from the `listing_visibility` enum
  // column. Null/missing collapses to `public` — matches the DB default and
  // covers historic row snapshots taken before the column existed.
  return row.visibility ?? "public";
}

/**
 * Parse a raw `listings` row into the canonical `Listing` ADT. Throws
 * `ListingParseError` (or a Zod ZodError for shape failures) on invalid
 * input — callers handle at the boundary.
 *
 * This is the ONLY legitimate constructor for `Listing` values past Phase 1.
 * Hand-rolled literals in consumers are an anti-pattern (single-producer
 * discipline — same rationale as `parseShippoRate` for the rate seam).
 *
 * Per-variant invariants enforced:
 *  - `fixed_price` / `fixed_price_with_offers` → `price != null`
 *  - `auction` → `auction_start_price != null` AND `auction_end_at != null`
 *  - `preorder` → `listing_format='fixed_price'` AND `price != null` AND
 *                 `preorder_status != null` (mirrors
 *                 `listings_preorder_status_consistency_chk` DB CHECK)
 */
export function parseListingFromRow(input: unknown): Listing {
  const row = RawListingRowSchema.parse(input);
  const productionType: ListingType = row.type ?? row.listing_type ?? "standard";

  const base: ListingBase = {
    id: row.id,
    sellerId: row.seller_id,
    currency: row.currency,
    visibility: visibilityFromRow(row),
    shipping: parseShippingFromRow(row),
  };

  // Preorder is its own variant — drains the `type='preorder'` axis before
  // dispatching on listing_format. Today's prod has 0 preorder × non-fp rows.
  if (productionType === "preorder") {
    if (row.listing_format !== "fixed_price") {
      throw new ListingParseError(
        `type='preorder' requires listing_format='fixed_price', got '${row.listing_format}'`,
      );
    }
    if (row.price == null) {
      throw new ListingParseError("type='preorder' requires price");
    }
    if (row.preorder_status == null) {
      throw new ListingParseError(
        "type='preorder' requires preorder_status (mirrors listings_preorder_status_consistency_chk)",
      );
    }
    return {
      ...base,
      format: "fixed_price",
      type: "preorder",
      price: row.price,
      preorderStatus: row.preorder_status,
      preorderDeadline: row.preorder_deadline ?? null,
    };
  }

  switch (row.listing_format) {
    case "fixed_price": {
      if (row.price == null) {
        throw new ListingParseError("listing_format='fixed_price' requires price");
      }
      return {
        ...base,
        format: "fixed_price",
        type: "standard",
        price: row.price,
      };
    }
    case "fixed_price_with_offers": {
      if (row.price == null) {
        throw new ListingParseError(
          "listing_format='fixed_price_with_offers' requires price",
        );
      }
      return {
        ...base,
        format: "fixed_price_with_offers",
        type: "standard",
        price: row.price,
      };
    }
    case "offers_only": {
      return {
        ...base,
        format: "offers_only",
        type: "standard",
      };
    }
    case "auction": {
      // Phase 5 (2026-06-30): auction state can arrive via three shapes:
      //   1. Nested embed `listing_auctions: { start_price, end_at, ... }`
      //      (`.from("listings").select("..., listing_auctions(...)")` —
      //      PostgREST may return `T | T[]` depending on hint).
      //   2. Flat aliased `auction_*` keys (rows from
      //      `listings_with_effective_published_at` view, which re-exports
      //      the side table under legacy names).
      //   3. Pre-Phase-5 raw `listings.auction_*` rows (legacy fixtures,
      //      backups, integration tests captured before the migration).
      // We try (1) first, fall back to (2) — both produce the same ADT.
      const rawEmbed = row.listing_auctions;
      const embed = Array.isArray(rawEmbed) ? (rawEmbed[0] ?? null) : (rawEmbed ?? null);
      const startPrice =
        (embed && embed.start_price != null) ? embed.start_price : row.auction_start_price;
      const reservePrice =
        (embed && embed.reserve_price != null) ? embed.reserve_price : row.auction_reserve_price;
      const endAt =
        (embed && embed.end_at != null) ? embed.end_at : row.auction_end_at;
      if (startPrice == null) {
        throw new ListingParseError(
          "listing_format='auction' requires auction.start_price (embed or auction_start_price alias)",
        );
      }
      if (endAt == null) {
        throw new ListingParseError(
          "listing_format='auction' requires auction.end_at (embed or auction_end_at alias)",
        );
      }
      return {
        ...base,
        format: "auction",
        type: "standard",
        startPrice,
        reservePrice: reservePrice ?? null,
        endAt,
      };
    }
    default:
      // Exhaustiveness probe — extending LISTING_FORMATS without extending
      // this switch fails compile here (the case list is the same tuple).
      return unreachableListingFormat(row.listing_format);
  }
}

function unreachableListingFormat(x: never): never {
  throw new ListingParseError(`unhandled listing_format: ${String(x)}`);
}

/**
 * Inverse of `parseListingFromRow` — flattens an ADT instance back to the
 * snake_case raw-row shape. Useful for round-trip tests and migration
 * scaffolding (Phase 3-7 will read ADTs and persist via this serializer).
 *
 * Round-trip identity holds at the ADT level:
 *   `parseListingFromRow(serializeListingToRow(l))` deep-equals `l`.
 * Wire-level round-trip is NOT guaranteed (the parser normalises nullable
 * legacy fields to canonical values, e.g. `visibility=null` →
 * `visibility='public'` on the way back; same reason `shipping_mode=null`
 * legacy rows collapse to `{ mode: 'legacy' }`).
 */
export function serializeListingToRow(l: Listing): RawListingRow {
  const base = {
    id: l.id,
    seller_id: l.sellerId,
    currency: l.currency as RawListingRow["currency"],
    listing_format: l.format as RawListingRow["listing_format"],
    type: l.type,
    listing_type: null,
    // Phase 4 (2026-06-29): serialise the visibility axis directly. The
    // boolean `is_chat_private` column was dropped; producers write the
    // `visibility` enum value.
    visibility: l.visibility,
    ...shippingFieldsToRow(l.shipping),
  } satisfies Partial<RawListingRow>;

  switch (l.format) {
    case "fixed_price": {
      if (l.type === "preorder") {
        return {
          ...base,
          price: l.price,
          auction_start_price: null,
          auction_reserve_price: null,
          auction_end_at: null,
          preorder_status: l.preorderStatus,
          preorder_deadline: l.preorderDeadline,
        };
      }
      return {
        ...base,
        price: l.price,
        auction_start_price: null,
        auction_reserve_price: null,
        auction_end_at: null,
        preorder_status: null,
        preorder_deadline: null,
      };
    }
    case "fixed_price_with_offers": {
      return {
        ...base,
        price: l.price,
        auction_start_price: null,
        auction_reserve_price: null,
        auction_end_at: null,
        preorder_status: null,
        preorder_deadline: null,
      };
    }
    case "offers_only": {
      return {
        ...base,
        price: null,
        auction_start_price: null,
        auction_reserve_price: null,
        auction_end_at: null,
        preorder_status: null,
        preorder_deadline: null,
      };
    }
    case "auction": {
      return {
        ...base,
        price: null,
        auction_start_price: l.startPrice,
        auction_reserve_price: l.reservePrice,
        auction_end_at: l.endAt,
        preorder_status: null,
        preorder_deadline: null,
      };
    }
    default:
      return unreachableListing(l);
  }
}

function shippingFieldsToRow(s: ListingShipping): {
  shipping_mode: "legacy" | "live_rate";
  shipping_config: unknown[];
  carrier_provider: RawListingRow["carrier_provider"];
  parcel_weight_grams: number | null;
  parcel_length_mm: number | null;
  parcel_width_mm: number | null;
  parcel_height_mm: number | null;
} {
  if (s.mode === "live_rate") {
    return {
      shipping_mode: "live_rate",
      shipping_config: [],
      carrier_provider: s.provider as RawListingRow["carrier_provider"],
      parcel_weight_grams: s.parcel.weightGrams,
      parcel_length_mm: s.parcel.lengthMm,
      parcel_width_mm: s.parcel.widthMm,
      parcel_height_mm: s.parcel.heightMm,
    };
  }
  return {
    shipping_mode: "legacy",
    shipping_config: s.methods,
    carrier_provider: null,
    parcel_weight_grams: null,
    parcel_length_mm: null,
    parcel_width_mm: null,
    parcel_height_mm: null,
  };
}

// ---------------------------------------------------------------------------
// 5. Helper getters — pure, derived from ADT
// ---------------------------------------------------------------------------

/**
 * True when buyers can submit offers. Mirrors the legacy
 * `accepts_offers === true OR listing_format IN ('fixed_price_with_offers',
 * 'offers_only')` collapse documented in IMPACT §2 Item 3 — under the ADT,
 * the boolean column is redundant with the format discriminator.
 *
 * Phase 3 (2026-06-29) drops the `listings.accepts_offers` column entirely;
 * every consumer derives offer-enabled-ness from `listing_format` via this
 * helper or its format-only sibling {@link isOfferEnabledFormat}. Pinned by
 * `tests/unit/fitness-is-offer-enabled.test.ts`.
 */
export function isOfferEnabled(listing: Listing): boolean {
  return isOfferEnabledFormat(listing.format);
}

/**
 * Format-only variant of {@link isOfferEnabled} — pure function of the
 * `listing_format` discriminator. Used by callers that have the format
 * literal in hand but not a full parsed `Listing` (delivery shells,
 * builder validators, UI inputs reading the form state). Single source of
 * truth for the "offers enabled" predicate post Phase 3 collapse.
 *
 * @see .tondocs/listings/DISCRIMINATORS_HYGIENE_IMPACT.md §2 Item 3
 */
export function isOfferEnabledFormat(format: ListingFormat): boolean {
  return format === "fixed_price_with_offers" || format === "offers_only";
}

export function isAuction(listing: Listing): listing is AuctionListing {
  return listing.format === "auction";
}

export function isPreorder(listing: Listing): listing is PreorderListing {
  return listing.type === "preorder";
}

export function isChatPrivate(listing: Listing): boolean {
  return listing.visibility === "chat_private";
}

/**
 * Exhaustiveness probe — every `switch (l.format)` (or similar narrowing
 * over `Listing`) should land in `default: unreachableListing(l)` so that
 * extending the union without extending the switch fails compile.
 *
 * Same pattern as the inline `unreachable` probes scattered across
 * `lib/payments/*` and `lib/orders/decide-order-status-transition.ts`.
 */
export function unreachableListing(x: never): never {
  throw new Error(`unhandled Listing variant: ${JSON.stringify(x)}`);
}
